<template>
  <div>
    <v-container class="pb-0">
      <v-row class="my-0 mb-2">
        <v-col cols="3" class="py-0 pr-1">
          <v-select
            :background-color="filters.company.length ? 'serenade' : ''"
            v-model="filters.company"
            :items="companies"
            :loading="companiesLoading"
            :disabled="companiesLoading"
            item-text="title"
            item-value="id"
            dense
            hide-details
            outlined
            clearable
            multiple
            :placeholder="'company'|localize"
            :menu-props="{ offsetY: true, bottom: true }"
            @change="clearRequestList();getRequests();"
          >
            <template v-slot:item="{ item, on }">
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </template>
            <template v-slot:selection="{ item, index }">
              <div class="d-flex">
                <span v-if="index === 0" class="text-truncate" style="max-width: 100px">{{ item.title }}</span>
                <span v-if="index === 1" class="grey--text text-caption">+{{ filters.company.length - 1 }}</span>
              </div>
            </template>
          </v-select>
        </v-col>
        <v-col cols="3" class="py-0">
          <v-row class="my-0">
            <v-col cols="6" class="py-0 px-1">
              <v-menu
                ref="menuDateFrom"
                v-model="menuDateFrom"
                :close-on-content-click="false"
                :return-value.sync="filters.dateFrom"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :background-color="filters.dateFrom ? 'serenade' : ''"
                    :placeholder="'dateFrom' | localize"
                    :value="filters.dateFrom"
                    readonly
                    dense
                    hide-details
                    outlined
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.dateFrom"
                  no-title
                  scrollable
                  @input="$refs.menuDateFrom.save(filters.dateFrom)"
                  @change="clearRequestList();getRequests();"
                />
              </v-menu>
            </v-col>
            <v-col cols="6" class="py-0 px-1">
              <v-menu
                ref="menuDateTo"
                v-model="menuDateTo"
                :close-on-content-click="false"
                :return-value.sync="filters.dateTo"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    :background-color="filters.dateTo ? 'serenade' : ''"
                    :placeholder="'dateTo' | localize"
                    :value="filters.dateTo"
                    readonly
                    dense
                    hide-details
                    outlined
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="filters.dateTo"
                  no-title
                  scrollable
                  @input="$refs.menuDateTo.save(filters.dateTo)"
                  @change="clearRequestList();getRequests();"
                />
              </v-menu>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="3" class="py-0 px-1">
          <v-select
            :background-color="filters.transport_mode.length ? 'serenade' : ''"
            v-model="filters.transport_mode"
            :items="transportModeList"
            item-text="name"
            item-value="value"
            dense
            hide-details
            outlined
            clearable
            multiple
            :placeholder="'transportMode'|localize"
            :menu-props="{ offsetY: true, bottom: true }"
            @change="clearRequestList();getRequests();"
          >
            <template v-slot:item="{ item, on }">
              <v-icon class="mr-2" color="silver-chalice">{{ TRANSPORT_ICONS[item['value']] }}</v-icon>
              <v-list-item-title>{{ item.value|localize }}</v-list-item-title>
            </template>
            <template v-slot:selection="{ item, index }">
              <v-icon v-if="index <= 2" class="mr-2" color="silver-chalice">{{ TRANSPORT_ICONS[item['value']] }}</v-icon>
              <span v-if="index === 3" class="grey--text text-caption">+{{ filters.transport_mode.length - 3 }}</span>
            </template>
          </v-select>
        </v-col>
        <v-col cols="3" class="py-0 pl-1">
          <v-select
            :background-color="filters.request_type && 'serenade'"
            v-model="filters.request_type"
            :items="requestTypesList"
            item-text="name"
            item-value="value"
            dense
            hide-details
            outlined
            clearable
            :placeholder="'requestType'|localize"
            :menu-props="{ offsetY: true, bottom: true }"
            @change="clearRequestList();getRequests();"
          />
        </v-col>
      </v-row>
    </v-container>
    <v-data-table
      :headers="headers"
      :items="requestList"
      :loading="loading"
      loader-height="2"
      :sort-by="defaultSortBy"
      :sort-desc="defaultSortDesc"
      :options.sync="options"
      :server-items-length="totalItems"
      hide-default-footer
    >
      <template v-slot:no-data>
        <EmptyState icon="mdi-inbox-outline" :title="'emptyGenericTitle'|localize" :subtitle="'emptyGenericSubtitle'|localize"/>
      </template>
      <template v-slot:item.percent_of_bookings_offers="{ item }">
        <div v-if="item.percent_of_bookings_offers">{{ item.percent_of_bookings_offers }}%</div>
      </template>
    </v-data-table>
    <v-pagination
      color="primary"
      v-model="currentPage"
      :length="totalPages"
      :total-visible="7"
      class="py-6"
      :disabled="loading"
      @input="requestList = [];getRequests();"
    />
  </div>
</template>

<script>
  import {TRANSPORT_ICONS} from '@/enums/transportMode';
  import api from '@/api/api';
  import localize from '@/filters/localize';
  import methods from '@/helpers/methods';
  import EmptyState from '@/components/common/EmptyState';

  export default {
    name: 'AdminPerformanceReports',
    components: {EmptyState},
    props: ['companies', 'companiesLoading', 'tab'],

    data() {
      return {
        menuDateFrom: false,
        menuDateTo: false,
        loading: false,
        defaultSortBy: 'company',
        defaultSortDesc: false,
        options: {},
        itemsPerPage: 20,
        currentPage: 1,
        totalPages: 0,
        totalItems: 0,
        filters: {
          dateFrom: methods.yearAgoDate(),
          dateTo: methods.todayDate(),
          company: [],
          transport_mode: [],
          request_type: null,
        },
        requestList: [],
      };
    },

    watch: {
      options: {
        handler() {
          this.clearRequestList();
          this.getRequests();
        },
        deep: true,
      },
    },

    computed: {
      TRANSPORT_ICONS() {
        return TRANSPORT_ICONS;
      },
      requestTypesList() {
        return this.$store.getters.requestTypesList;
      },
      transportModeList() {
        return this.$store.getters.transportModeList;
      },
      headers() {
        return [
          {text: localize('company'), value: 'company', width: 125},
          {text: localize('totalNumberOfRequests'), value: 'total_number_of_request', width: 100},
          {text: localize('requestsWithOffers'), value: 'requests_with_offers', width: 110},
          {text: localize('totalNumberOfOffers'), value: 'total_numbers_of_offers', width: 100},
          {text: localize('totalNumberOfBookedOffers'), value: 'total_numbers_of_booked_offers', width: 100},
          {text: localize('bookingsOfOffers'), value: 'percent_of_bookings_offers', width: 140},
          {text: localize('averageFirstResponseTime'), value: 'average_first_response_time', width: 100},
          {text: localize('averageFirstResponseTimeDuringBusinessHours'), value: 'average_first_response_time_business_hours', width: 140},
          {text: localize('averageNumberOfQuotesPerReply'), value: 'average_numbers_of_quotes_per_reply', width: 140},
        ];
      },
    },

    methods: {
      clearRequestList() {
        this.requestList = [];
        this.totalPages = 0;
        this.totalItems = 0;
        this.currentPage = 1;
      },
      queryData() {
        let queryData = {};
        queryData['per_page'] = this.itemsPerPage;
        queryData['page'] = this.currentPage;
        this.options.sortBy[0] && (
          queryData['sort'] = {
            field: this.options.sortBy[0],
            desc: this.options.sortDesc[0],
          });
        queryData.filters = {};
        this.filters.company.length && (queryData.filters.company = this.filters.company);
        if (this.filters.dateFrom || this.filters.dateTo) {
          queryData.filters.date = ["", ""];
          this.filters.dateFrom && (queryData.filters.date[0] = this.filters.dateFrom);
          this.filters.dateTo && (queryData.filters.date[1] = this.filters.dateTo);
        }
        this.filters.transport_mode.length && (queryData.filters.transport_mode = this.filters.transport_mode);
        this.filters.request_type && (queryData.filters.request_type = [this.filters.request_type]);
        return queryData;
      },
      async getRequests() {
        try {
          this.loading = true;
          const performanceResponse = await api.getRequestBookingPerformance(this.queryData());
          this.setRequestsData(performanceResponse);
        } catch (e) {} finally {
          this.loading = false;
        }
      },
      setRequestsData(requestsData) {
        this.requestList = requestsData.data?.data || [];
        this.totalPages = requestsData.data.pagination['total_page'];
        this.totalItems = requestsData.data.pagination['total_items'];
      },
    }
  };
</script>
